import React from 'react';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING } from '../../styles/emziCI';
import StructuredListInput from './StructuredListInput';
import KeyValuePairInput from './KeyValuePairInput';
import { BILINGUAL_REQUIRED } from '../../utils/formValidation';

/**
 * EnhancedComplianceForm - Compliance pack form with Input Quality Layer enhancements
 * Focus: Regulations, prohibited phrases, safe alternatives, legal requirements
 */
const EnhancedComplianceForm = ({ 
  content = {}, 
  onChange, 
  errors = {}, 
  touched = {},
  onFieldBlur,
  packType = 'compliance'
}) => {
  const handleChange = (field, value) => {
    onChange({ ...content, [field]: value });
  };

  const requiresBilingual = BILINGUAL_REQUIRED[packType];

  return (
    <div style={{ 
      display: 'flex',
      flexDirection: 'column',
      gap: EMZI_SPACING.md,
    }}>
      {/* Regulation Name */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Regulation Name
          <span style={{ color: EMZI_COLORS.error, marginLeft: '4px' }}>*</span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 Official name of the regulation or law. Example: "Malaysian Food Safety Act 2015" or "JAKIM Halal Certification Guidelines"
        </div>

        <input
          type="text"
          value={content.regulation_name || ''}
          onChange={(e) => handleChange('regulation_name', e.target.value)}
          onBlur={() => onFieldBlur && onFieldBlur('regulation_name')}
          placeholder="e.g., Malaysian Food Safety Act 2015"
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${touched.regulation_name && errors.content?.regulation_name ? EMZI_COLORS.error : EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            outline: 'none',
          }}
        />
        {touched.regulation_name && errors.content?.regulation_name && (
          <div style={{
            marginTop: '4px',
            fontSize: '12px',
            color: EMZI_COLORS.error,
          }}>
            {errors.content.regulation_name}
          </div>
        )}
      </div>

      {/* Authority/Issuing Body */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Authority/Issuing Body
          <span style={{
            fontSize: '11px',
            color: EMZI_COLORS.textMuted,
            marginLeft: EMZI_SPACING.xs,
            fontWeight: '400',
          }}>
            (Optional)
          </span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 Who enforces this regulation. Example: "JAKIM" or "Ministry of Health Malaysia"
        </div>

        <input
          type="text"
          value={content.authority || ''}
          onChange={(e) => handleChange('authority', e.target.value)}
          placeholder="e.g., JAKIM, Ministry of Health Malaysia"
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            outline: 'none',
          }}
        />
      </div>

      {/* Prohibited Phrases - Structured List */}
      <StructuredListInput
        label="Prohibited Phrases"
        items={content.prohibited_phrases || []}
        onChange={(items) => handleChange('prohibited_phrases', items)}
        minItems={1}
        maxItems={30}
        placeholder="Add a prohibited phrase..."
        helper="Words or phrases that MUST NOT be used. Example: 'Guaranteed cure' or 'Miraculous results' or 'No side effects'"
        required={true}
        hasError={touched.prohibited_phrases && errors.content?.prohibited_phrases}
        errorMessage={errors.content?.prohibited_phrases}
      />

      {/* Safe Alternatives - Key-Value Pairs */}
      <KeyValuePairInput
        label="Safe Alternatives (Prohibited → Safe)"
        pairs={content.safe_alternatives || {}}
        onChange={(pairs) => handleChange('safe_alternatives', pairs)}
        keyLabel="Prohibited phrase"
        valueLabel="Safe alternative"
        helper="Map prohibited phrases to compliant alternatives. Example: 'Guaranteed cure' → 'May help support wellness'"
        required={true}
      />

      {/* Mandatory Phrases - Optional */}
      <StructuredListInput
        label="Mandatory Phrases/Disclaimers"
        items={content.mandatory_phrases || []}
        onChange={(items) => handleChange('mandatory_phrases', items)}
        minItems={0}
        maxItems={15}
        placeholder="Add a mandatory phrase..."
        helper="Required disclaimers or statements. Example: 'Individual results may vary' or 'Not evaluated by FDA'"
        required={false}
        hasError={false}
      />

      {/* Context/Scope */}
      <div style={{ marginBottom: EMZI_SPACING.md }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '500',
          marginBottom: EMZI_SPACING.xs,
          color: EMZI_COLORS.textPrimary,
        }}>
          Context/Scope
          <span style={{
            fontSize: '11px',
            color: EMZI_COLORS.textMuted,
            marginLeft: EMZI_SPACING.xs,
            fontWeight: '400',
          }}>
            (Optional)
          </span>
        </label>
        
        <div style={{
          fontSize: '12px',
          color: EMZI_COLORS.textSecondary,
          marginBottom: EMZI_SPACING.xs,
          padding: EMZI_SPACING.xs,
          background: EMZI_COLORS.surface,
          borderRadius: EMZI_RADIUS.sm,
          borderLeft: `3px solid ${EMZI_COLORS.primary}`,
        }}>
          💡 When/where this compliance applies. Example: "Applies to all health supplement marketing materials and agent conversations"
        </div>

        <textarea
          value={content.context || ''}
          onChange={(e) => handleChange('context', e.target.value)}
          placeholder="e.g., Applies to all health supplement marketing materials and agent conversations"
          rows={3}
          style={{
            width: '100%',
            padding: EMZI_SPACING.sm,
            border: `1px solid ${EMZI_COLORS.border}`,
            borderRadius: EMZI_RADIUS.md,
            fontSize: '14px',
            fontFamily: 'inherit',
            outline: 'none',
            resize: 'vertical',
          }}
        />
      </div>

      {/* Penalties/Consequences - Optional */}
      <StructuredListInput
        label="Penalties/Consequences"
        items={content.penalties || []}
        onChange={(items) => handleChange('penalties', items)}
        minItems={0}
        maxItems={8}
        placeholder="Add a penalty..."
        helper="What happens if violated. Example: 'Fine up to RM50,000' or 'License revocation'"
        required={false}
        hasError={false}
      />

      {/* Critical warning */}
      <div style={{
        padding: EMZI_SPACING.sm,
        background: '#fef2f2',
        borderRadius: EMZI_RADIUS.sm,
        fontSize: '12px',
        color: EMZI_COLORS.error,
        borderLeft: `3px solid ${EMZI_COLORS.error}`,
      }}>
        ⚠️ <strong>Legal Safety:</strong> Compliance packs protect the business. Always require bilingual entry (EN & MS) and ensure safe alternatives are clear and actionable.
      </div>
    </div>
  );
};

export default EnhancedComplianceForm;
